import type { TFunction } from "i18next";
import type { SingleSelectOption } from "@/components/controls/SingleSelect";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { getLocalizedCountryOptions } from "@/utils/countries";
import { formatFullDate } from "@/utils/datetime";
import {
  NEGATIVE_BOOL_TOKENS,
  POSITIVE_BOOL_TOKENS
} from "@/utils/fieldRendering/legacyBoolTokens";
import { getCurrentLocale } from "@/utils/locale";

const BOOLEAN_FIELD_TYPES = new Set<string>([
  FieldDisplayType.BOOL,
  FieldDisplayType.SWITCH,
  FieldDisplayType.CHECKBOX,
  FieldDisplayType.CHECKLIST_ITEM
]);

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}/;

const isEmpty = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

// Legacy Dreamer rows store booleans as "yes" / "checked" strings.
const booleanText = (value: unknown, t: TFunction): string | null => {
  const token =
    typeof value === "boolean"
      ? String(value)
      : typeof value === "string"
        ? value.trim().toLowerCase()
        : null;
  if (token === null) return null;
  if (POSITIVE_BOOL_TOKENS.has(token)) return t("deals.dealView.yes");
  if (NEGATIVE_BOOL_TOKENS.has(token)) return t("deals.dealView.no");
  return null;
};

const dateText = (value: unknown): string =>
  typeof value === "string" && DATE_ONLY.test(value)
    ? formatFullDate(value)
    : String(value);

const optionLabel = (id: unknown, options: SingleSelectOption[]): string =>
  options.find((option) => option.id === id)?.displayName ?? String(id);

/**
 * A stored answer as plain text, for showing it where it can't be edited.
 * Options are the item's own select options or company people.
 */
export const responseValueText = (
  fieldType: string,
  value: unknown,
  options: SingleSelectOption[],
  t: TFunction
): string => {
  if (isEmpty(value))
    return t("contracts.detailedView.onboarding.dreamer.suggestion.noAnswer");

  if (BOOLEAN_FIELD_TYPES.has(fieldType)) {
    return booleanText(value, t) ?? String(value);
  }

  switch (fieldType) {
    case FieldDisplayType.SINGLE_SELECT:
    case FieldDisplayType.PEOPLE_IN_COMPANY:
      return optionLabel(value, options);
    case FieldDisplayType.MULTI_SELECT:
      return Array.isArray(value)
        ? value.map((id) => optionLabel(id, options)).join(", ")
        : optionLabel(value, options);
    case FieldDisplayType.DATE:
      return dateText(value);
    case FieldDisplayType.DATE_RANGE: {
      const range = value as { fromDate?: unknown; toDate?: unknown };
      if (typeof value !== "object" || !range.fromDate || !range.toDate) {
        return String(value);
      }
      return `${dateText(range.fromDate)} – ${dateText(range.toDate)}`;
    }
    case FieldDisplayType.CHECKBOX_WITH_REQUIRED_TEXT: {
      const answer = value as { checked?: unknown; text?: unknown };
      if (typeof value !== "object") return String(value);
      const checked = booleanText(Boolean(answer.checked), t);
      return answer.text ? `${checked}: ${String(answer.text)}` : `${checked}`;
    }
    case FieldDisplayType.COUNTRIES:
      return (
        getLocalizedCountryOptions(getCurrentLocale()).find(
          (country) => country.code === value
        )?.label ?? String(value)
      );
    case FieldDisplayType.FILE_UPLOAD:
      return Array.isArray(value)
        ? value
            .map((file) =>
              file && typeof file === "object" && "filename" in file
                ? String((file as { filename: unknown }).filename)
                : String(file)
            )
            .join(", ")
        : String(value);
    default:
      return String(value);
  }
};
